import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { getSubscriptions, removeSubscription } from '../utils/api.js'
import { getTelegramUser, hapticImpact, getActivityEmoji } from '../utils/telegram.js'

function normalizeActivityType(type) {
  return String(type || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_')
}

function isEventSub(sub) {
  return String(sub.venue_id || '').startsWith('event:')
}

function getSubIcon(sub) {
  if (isEventSub(sub)) return '🎟'
  const type = normalizeActivityType(sub.activity_type)
  return getActivityEmoji(type)
}

function getSubTypeLabel(sub) {
  if (isEventSub(sub)) return 'specific event'
  const type = sub.activity_type || ''
  return type.replace(/_/g, ' ') || 'venue'
}

function SubChip({ sub, onRemove, removing }) {
  const icon = getSubIcon(sub)
  const typeLabel = getSubTypeLabel(sub)
  const name = sub.venue_name || sub.venue_id

  return (
    <div className="sub-chip">
      <div className="sub-chip-icon">{icon}</div>
      <div className="sub-chip-body">
        <div className="sub-chip-name" title={name}>{name}</div>
        <div className="sub-chip-type">{typeLabel}</div>
      </div>
      <button
        className="sub-chip-remove"
        onClick={() => onRemove(sub)}
        disabled={removing}
        aria-label={`Remove subscription to ${name}`}
      >
        {removing ? '…' : '×'}
      </button>
    </div>
  )
}

export default function ProfilePage() {
  const navigate = useNavigate()
  const [subscriptions, setSubscriptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [removing, setRemoving] = useState(null) // venue_id being removed
  const [alert, setAlert] = useState(null)

  const user = getTelegramUser()

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getSubscriptions()
      setSubscriptions(res.data.data || [])
    } catch (err) {
      setAlert({ type: 'error', text: err?.response?.data?.error || 'Failed to load subscriptions' })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleRemove = async (sub) => {
    hapticImpact('medium')
    setRemoving(sub.venue_id)
    try {
      await removeSubscription(sub.venue_id)
      setSubscriptions(prev => prev.filter(s => s.id !== sub.id))
      setAlert({ type: 'success', text: `Removed "${sub.venue_name || sub.venue_id}"` })
    } catch (err) {
      setAlert({ type: 'error', text: err?.response?.data?.error || 'Failed to remove' })
    } finally {
      setRemoving(null)
    }
  }

  // Group subscriptions: brand subs vs event subs
  const brandSubs = subscriptions.filter(s => !isEventSub(s))
  const eventSubs = subscriptions.filter(s => isEventSub(s))

  return (
    <div>
      {/* User header */}
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: 'var(--ds-accent-muted)',
            border: '1px solid var(--ds-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 20,
            flexShrink: 0
          }}>
            {user?.photo_url
              ? <img src={user.photo_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
              : '👤'
            }
          </div>
          <div>
            <div className="page-header-title" style={{ fontSize: 18 }}>
              {user?.first_name || 'Profile'}
              {user?.last_name ? ` ${user.last_name}` : ''}
            </div>
            {user?.username && (
              <div className="page-header-subtitle">@{user.username}</div>
            )}
          </div>
        </div>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => navigate('/settings')}
          aria-label="Settings"
        >
          ⚙ Settings
        </button>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type}`} style={{ marginTop: 4 }}>
          {alert.text}
          <button
            style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 16, lineHeight: 1 }}
            onClick={() => setAlert(null)}
          >×</button>
        </div>
      )}

      {loading ? (
        <div className="status-message"><div className="loading-spinner" /></div>
      ) : subscriptions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🔔</div>
          <div className="empty-state-title">No subscriptions yet</div>
          <div className="empty-state-text">
            Browse the All tab and subscribe to brands or events to receive automatic updates.
          </div>
        </div>
      ) : (
        <>
          {brandSubs.length > 0 && (
            <div>
              <div className="group-header">Brand subscriptions</div>
              {brandSubs.map(sub => (
                <SubChip
                  key={sub.id}
                  sub={sub}
                  onRemove={handleRemove}
                  removing={removing === sub.venue_id}
                />
              ))}
            </div>
          )}

          {eventSubs.length > 0 && (
            <div>
              <div className="group-header">Event subscriptions</div>
              {eventSubs.map(sub => (
                <SubChip
                  key={sub.id}
                  sub={sub}
                  onRemove={handleRemove}
                  removing={removing === sub.venue_id}
                />
              ))}
            </div>
          )}

          <p style={{ fontSize: 12, color: 'var(--ds-text-secondary)', textAlign: 'center', marginTop: 16, lineHeight: 1.5 }}>
            You&apos;ll receive a Telegram message when subscribed venues or events have updates.
          </p>
        </>
      )}
    </div>
  )
}
